/**
 * Wires the pure `alertEvaluator` to a stat tile's settled result (S12).
 * Called from `StatTileView`, which stays MOUNTED while its workspace tab is
 * hidden (`Workspace.tsx` toggles a `hidden` class, never unmounts) — so a
 * `background: true` alert keeps evaluating on every scheduler-driven
 * background refetch even when the dashboard isn't the active tab. This
 * hook only reacts to NEW settled values; scheduling/eligibility stays
 * entirely in `useDashboardScheduler`/`refreshScheduler`.
 */
import { useEffect } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { StatTile } from '../types/dashboardTypes';
import { evaluateAlert } from '../services/alertEvaluator';
import { trackDashboardAlertFired } from '../services/dashboardRuntimeTelemetry';
import { useDashboardAlertStore } from '../store/dashboardAlertStore';

export type TileAlertBadgeStatus = 'firing' | 'armed' | undefined;

/** `value` is the resolved numeric scalar for this settle, or `undefined`
 * when the tile has no resolvable numeric value (empty/misconfigured). */
export function useTileAlert(dashboardId: string, tile: StatTile, value: number | undefined): TileAlertBadgeStatus {
  const { track } = useAnalytics();
  const rule = tile.alertRule;
  const persisted = useDashboardAlertStore((s) => s.byDashboard[dashboardId]?.[tile.id]);
  const loaded = useDashboardAlertStore((s) => s.loaded.has(dashboardId));
  const ensureLoaded = useDashboardAlertStore((s) => s.ensureLoaded);
  const setTileState = useDashboardAlertStore((s) => s.setTileState);

  useEffect(() => {
    if (rule) void ensureLoaded(dashboardId);
  }, [rule, dashboardId, ensureLoaded]);

  useEffect(() => {
    if (!rule || !loaded || value === undefined) return;

    const { nextState, shouldNotify } = evaluateAlert(rule, persisted, value, new Date());
    const changed =
      !persisted ||
      persisted.status !== nextState.status ||
      persisted.lastFiredAt !== nextState.lastFiredAt ||
      persisted.clearedAt !== nextState.clearedAt;
    // `changed` also re-runs this effect once (via the `persisted` dep, after
    // `setTileState` lands) to confirm the sustained/no-op case — that
    // re-entry is idempotent (evaluateAlert against its own fresh output
    // never re-notifies), so it settles without looping.
    if (changed) void setTileState(dashboardId, tile.id, nextState);

    if (shouldNotify) {
      trackDashboardAlertFired(track, rule.background);
      void window.electronAPI?.dashboards
        ?.notifyAlert({
          dashboardId,
          tileId: tile.id,
          title: tile.title,
          body: `${tile.title} is ${rule.operator} ${rule.threshold}`,
          background: rule.background,
        })
        .catch(() => {});
    }
  }, [rule, loaded, value, persisted, dashboardId, tile.id, tile.title, track, setTileState]);

  if (!rule) return undefined;
  return persisted?.status ?? 'armed';
}
